import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Building2, TrendingUp, CheckCircle2, XCircle, Clock, Euro } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import Logo from '@/components/Logo';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyOwnerDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (!isAuthenticated() || user?.role !== 'property_owner') {
      navigate('/');
      return;
    }
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/offers/by-owner`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(response.data.offers || []);
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Errore nel caricamento delle offerte');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        icon: Clock,
        text: 'In Attesa',
        color: 'bg-amber-500',
        bgLight: 'bg-amber-50',
        borderColor: 'border-amber-200',
        textColor: 'text-amber-700'
      },
      accepted: {
        icon: CheckCircle2,
        text: 'Accettata',
        color: 'bg-emerald-500',
        bgLight: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        textColor: 'text-emerald-700'
      },
      rejected: {
        icon: XCircle,
        text: 'Rifiutata',
        color: 'bg-red-500',
        bgLight: 'bg-red-50',
        borderColor: 'border-red-200',
        textColor: 'text-red-700'
      }
    };
    return configs[status] || configs.pending;
  };

  const filteredOffers = offers.filter(offer => {
    if (activeTab === 'all') return true;
    return offer.status === activeTab;
  });

  const getStats = () => {
    return {
      total: offers.length,
      pending: offers.filter(o => o.status === 'pending').length,
      accepted: offers.filter(o => o.status === 'accepted').length,
      rejected: offers.filter(o => o.status === 'rejected').length
    };
  };

  const stats = getStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-slate-50">
      {/* Header */}
      <nav className="backdrop-blur-xl bg-white/80 border-b border-slate-200/50 px-6 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna alla Home
            </Button>
            <div className="h-6 w-px bg-slate-300" />
            <Logo size="small" showText={false} />
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
              <Building2 className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-900">{user?.full_name}</span>
            </div>
            <Button variant="outline" onClick={() => {
              logout();
              navigate('/');
            }}>
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Dashboard Offerte</h1>
          <p className="text-slate-600">Gestisci le tue offerte inviate ai viaggiatori</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 font-medium">Totale Offerte</p>
                <p className="text-3xl font-bold text-blue-900 mt-1">{stats.total}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-blue-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700 font-medium">In Attesa</p>
                <p className="text-3xl font-bold text-amber-900 mt-1">{stats.pending}</p>
              </div>
              <Clock className="w-10 h-10 text-amber-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-700 font-medium">Accettate</p>
                <p className="text-3xl font-bold text-emerald-900 mt-1">{stats.accepted}</p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-emerald-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700 font-medium">Rifiutate</p>
                <p className="text-3xl font-bold text-red-900 mt-1">{stats.rejected}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-600 opacity-50" />
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200">
          {[
            { key: 'all', label: `Tutte (${stats.total})` },
            { key: 'pending', label: `In Attesa (${stats.pending})` },
            { key: 'accepted', label: `Accettate (${stats.accepted})` },
            { key: 'rejected', label: `Rifiutate (${stats.rejected})` }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 font-medium transition-all ${
                activeTab === tab.key
                  ? 'text-orange-600 border-b-2 border-orange-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Offers List */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-orange-600 rounded-full animate-spin mx-auto"></div>
          </div>
        ) : filteredOffers.length === 0 ? (
          <Card className="p-16 text-center bg-white/50">
            <Building2 className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 text-lg">
              {activeTab === 'all' 
                ? 'Non hai ancora inviato offerte'
                : `Nessuna offerta ${getStatusConfig(activeTab).text.toLowerCase()}`}
            </p>
            <Button
              onClick={() => navigate('/')}
              className="mt-4 bg-gradient-to-r from-orange-600 to-orange-700"
            >
              Esplora Richieste
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredOffers.map((offer) => {
              const statusConfig = getStatusConfig(offer.status);
              const StatusIcon = statusConfig.icon;
              const request = offer.request_details;

              return (
                <Card
                  key={offer.id}
                  className="backdrop-blur-sm bg-white/80 border border-slate-200/50 p-6 hover:shadow-xl transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Badge className={`${statusConfig.color} text-white px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        {statusConfig.text}
                      </Badge>
                      <span className="text-sm text-slate-500">
                        {new Date(offer.created_at).toLocaleDateString('it-IT', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-black text-orange-600">€{offer.offered_price}</div>
                      <div className="text-xs text-slate-500 font-medium">prezzo offerto/notte</div>
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{offer.property_name}</h3>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <h4 className="font-semibold text-slate-900 mb-2 text-sm">Richiesta Viaggiatore</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-slate-600">Città:</span>
                        <span className="ml-2 font-medium text-slate-900">{request?.city || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-slate-600">Ospiti:</span>
                        <span className="ml-2 font-medium text-slate-900">{request?.guests || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-slate-600">Check-in:</span>
                        <span className="ml-2 font-medium text-slate-900">{request?.check_in_date || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-slate-600">Check-out:</span>
                        <span className="ml-2 font-medium text-slate-900">{request?.check_out_date || 'N/A'}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-slate-600 flex items-center gap-1">
                          <Euro className="w-4 h-4 text-emerald-600" />
                          Budget Range:
                        </span>
                        <span className="ml-2 font-bold text-emerald-600">
                          €{request?.budget_min || 0} - €{request?.budget_max || 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  {offer.room_description && (
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-slate-700 mb-1">Descrizione Camera:</p>
                      <p className="text-sm text-slate-600">{offer.room_description}</p>
                    </div>
                  )}

                  {offer.notes && (
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-slate-700 mb-1">Note:</p>
                      <p className="text-sm text-slate-600">{offer.notes}</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyOwnerDashboard;
